import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { getQueryStringResults } from '@plone/volto/actions';
import { Helmet } from '@plone/volto/helpers';
import { Plug } from '@plone/volto/components/manage/Pluggable';
import { Icon, Button } from '@redturtle/iovisit-theme/components';

import { useWhishlist } from '@redturtle/iovisit-theme/helpers';
import { PageHeader } from '@redturtle/iovisit-theme/components/Views';
import {
  Modal,
  Grid,
  Popup,
  Button as SemanticButton,
} from 'semantic-ui-react';
import Card from '@redturtle/iovisit-theme/components/Blocks/Listing/CardsTemplate/Card';
import leadimage from '@redturtle/iovisit-theme/components/Views/WhishlistPage/whishlist-leadimage.jpg';
import config from '@plone/registry';

const messages = defineMessages({
  wishlist_title: {
    id: 'wishlist_title',
    defaultMessage: 'La tua wishlist',
  },
  whishlist_info1: {
    id: 'whishlist_info1',
    defaultMessage:
      'Con la Wishlist, la lista dei desideri, puoi salvare contenuti interessanti, eventi a cui vuoi partecipare, tutto ciò che vuoi approfondire.',
  },
  whishlist_info2: {
    id: 'whishlist_info2',
    defaultMessage:
      "Usa l'icona a forma di stellina che trovi su tutti i contenuti del sito per salvarli e consultarli in seguito.",
  },
  whishlist_info3: {
    id: 'whishlist_info3',
    defaultMessage:
      'Per rimuovere un elemento dalla lista, clicca sulla stellina in corrispondenza del contenuto.',
  },
  wishlist_empty: {
    id: 'wishlist_empty',
    defaultMessage: 'La wishlist è vuota.',
  },
  delete_all: {
    id: 'whishlist_delete_all',
    defaultMessage: 'Elimina tutto',
  },
  confirm_delete_all: {
    id: 'whishlist_confirm_delete_all',
    defaultMessage:
      'Sei sicuro di volere eliminare tutti gli elementi presenti nella wishlist?',
  },
  no: {
    id: 'whishlist_delete_all_no',
    defaultMessage: 'No',
  },
  yes_delete_all: {
    id: 'whishlist_yes_delete_all',
    defaultMessage: 'Si, cancella tutto',
  },
  close_modal: {
    id: 'whishlist_close_modal',
    defaultMessage: 'Chiudi',
  },
  remove_from_whishlist: {
    id: 'remove_from_whishlist',
    defaultMessage: 'Rimuovi dalla wishlist',
  },
  add_to_whishlist: {
    id: 'add_to_whishlist',
    defaultMessage: 'Aggiungi ai preferiti',
  },
  print: {
    id: 'whishlist_print',
    defaultMessage: 'Stampa la wishlist',
  },
});

const WhishlistPage = ({ location }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const pathname = config.settings.isMultilingual ? '/' + intl.locale : '/';
  const { whishlist = [], clearWhishlist, toggleWhishlist } = useWhishlist();
  const [deleteAll, setDeleteAll] = useState(false);
  const [pressedTab, setPressedTab] = useState(false);
  const results = useSelector((state) => {
    return state.querystringsearch?.subrequests?.whishlist;
  });

  const [items, setItems] = useState([]);

  useEffect(() => {
    let res_items = results?.items ?? [];
    if (res_items.length > 0) {
      const reverseWhishlist = [...whishlist].reverse();
      res_items.sort((a, b) => {
        return (
          reverseWhishlist.indexOf(a['UID']) -
          reverseWhishlist.indexOf(b['UID'])
        );
      });
    }
    setItems(res_items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results?.items]);

  const handleFocusWishlist = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      document.querySelector('#wishlist-container').focus();
      setPressedTab(true);
    }
  };

  useEffect(() => {
    if (!pressedTab) {
      window.addEventListener('keydown', handleFocusWishlist);
      return () => {
        window.removeEventListener('keydown', handleFocusWishlist);
      };
    }
  }, [whishlist, pressedTab]);

  useEffect(() => {
    if (!results?.loading && whishlist?.length > 0) {
      dispatch(
        getQueryStringResults(
          pathname,
          {
            fullobjects: 1,
            query: [
              {
                i: 'UID',
                o: 'plone.app.querystring.operation.selection.any',
                v: whishlist,
              },
            ],
            b_size: 10000,
          },
          'whishlist',
          1,
        ),
      );

      // return dispatch({
      //   type: 'RESET_QUERYSTRING_RESULTS',
      //   subrequest: 'whishlist',
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div id="page-whishlist" className="ui container">
        <Helmet title={intl.formatMessage(messages.wishlist_title)} />
        <Plug pluggable="leadimage" id="leadimage-whishlist">
          <img
            src={leadimage}
            alt={intl.formatMessage(messages.wishlist_title)}
            loading="lazy"
          />
        </Plug>
        <PageHeader
          content={{
            title: intl.formatMessage(messages.wishlist_title),
            show_share_links: false,
          }}
        />

        <div className="whishlist-infos">
          <Icon prefix="far" icon="star" />
          <p>{intl.formatMessage(messages.whishlist_info1)}</p>
          <p>{intl.formatMessage(messages.whishlist_info2)}</p>
          <p>{intl.formatMessage(messages.whishlist_info3)}</p>
        </div>

        <div
          id="wishlist-container"
          role="region"
          tabIndex="-1"
          aria-live="polite"
        >
          {whishlist.length === 0 && (
            <h3
              tabIndex="-1"
              className="empty-list"
              label={intl.formatMessage(messages.wishlist_empty)}
            >
              {intl.formatMessage(messages.wishlist_empty)}
            </h3>
          )}

          {whishlist.length > 0 && (
            <div className="whishlist-list-wrapper" key={new Date().getTime()}>
              <div className="whishlist-actions">
                <Button
                  icon
                  size="mini"
                  onClick={(e) => {
                    e.preventDefault();
                    return window.print();
                  }}
                >
                  <Icon prefix="fas" icon="print" className="icon" />
                  {intl.formatMessage(messages.print)}
                </Button>
              </div>
              <div className="block listing">
                <div className="listing-template cards">
                  {items?.length > 0 && (
                    <Grid stackable>
                      {items.map((item, index) => (
                        <Grid.Column
                          tablet={12}
                          computer={6}
                          widescreen={4}
                          largeScreen={4}
                          key={index}
                        >
                          <Card item={item} show_thematic_area={false} />
                          <div className="whislist-item-actions">
                            <Popup
                              trigger={
                                <SemanticButton
                                  icon
                                  size="mini"
                                  primary
                                  onClick={() => {
                                    toggleWhishlist(item['UID']);
                                    items.splice(index, 1);
                                  }}
                                  title={
                                    intl.formatMessage(
                                      messages.remove_from_whishlist,
                                    ) +
                                    ' ' +
                                    item.title
                                  }
                                  animated="vertical"
                                  key={index + 'button'}
                                >
                                  <SemanticButton.Content hidden>
                                    <Icon
                                      prefix="fas"
                                      icon="trash"
                                      className="icon"
                                    />
                                  </SemanticButton.Content>

                                  <SemanticButton.Content visible>
                                    <Icon
                                      prefix="fas"
                                      icon="star"
                                      className="icon"
                                    />
                                  </SemanticButton.Content>
                                </SemanticButton>
                              }
                              on={['click', 'hover', 'focus']}
                              content={
                                intl.formatMessage(
                                  messages.remove_from_whishlist,
                                ) +
                                ' ' +
                                item.title
                              }
                              position="bottom center"
                              size="mini"
                              inverted
                            />
                          </div>
                        </Grid.Column>
                      ))}
                    </Grid>
                  )}

                  <div className="link-more">
                    <Button
                      primary
                      inverted
                      onClick={() => {
                        setDeleteAll(true);
                      }}
                    >
                      {intl.formatMessage(messages.delete_all)}
                    </Button>
                  </div>
                </div>
              </div>
              <Modal
                id="modalWhishlist"
                closeIcon
                onClose={() => setDeleteAll(false)}
                closeOnEscape={true}
                open={deleteAll}
                size="small"
                centered
              >
                <Modal.Content>
                  <Modal.Description>
                    {intl.formatMessage(messages.confirm_delete_all)}
                  </Modal.Description>
                </Modal.Content>

                <Modal.Actions>
                  <Button color="black" onClick={() => setDeleteAll(false)}>
                    {intl.formatMessage(messages.no)}
                  </Button>
                  <Button
                    content={intl.formatMessage(messages.yes_delete_all)}
                    primary
                    onClick={() => {
                      clearWhishlist();
                      setDeleteAll(false);
                    }}
                  />
                </Modal.Actions>
              </Modal>{' '}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WhishlistPage;
